import CloseRounded from "@mui/icons-material/CloseRounded";
import DragHandleRounded from "@mui/icons-material/DragHandleRounded";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { blueGrey, grey } from "@mui/material/colors";
import { isNil } from "lodash";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import Flx from "../../../components/layout/Flx";
import ScreenContent from "../../../components/layout/ScreenContent";
import Htag from "../../../components/typography/Htag";
import { sidebarClear } from "../../../redux/actions/sidebarActions";
import { BorderBottom } from "@mui/icons-material";

const COLLAPSE_THRESHOLD = 220; // Width in pixels below which sidebar will auto-close

const DrilldownSidebarPane = memo(
  ({
    title,
    children,

    fixedWidth,
    maxWidth = "80vw",
    initialWidth = "580px",
    minWidth = "200px",
    bodyOverflow = "auto",
    bodyPadding = 2,
    bodySx,
    sx,
    variant = "inline", // inline || fixed
    icon,
    titleBottomContent,
    headerSx,
    onCloseIcon = <CloseRounded />,
    onClose,
  }) => {
    const dispatch = useDispatch();
    const sidebarRef = useRef(null);
    const resizeDivRef = useRef(null);
    const [isResizing, setIsResizing] = useState(false);
    const [width, setWidth] = useState(initialWidth);
    const startXRef = useRef(0);
    const startWidthRef = useRef(0);

    // Convert string width values to numbers for calculations
    const getNumericWidth = (widthStr) => {
      if (typeof widthStr === "number") return widthStr;
      if (typeof widthStr !== "string") return 640; // Default fallback

      // Handle pixel values
      if (widthStr.endsWith("px")) {
        return parseInt(widthStr, 10);
      }
      // Handle vw values
      if (widthStr.endsWith("vw")) {
        const percentage = parseInt(widthStr, 10);
        return (percentage / 100) * window.innerWidth;
      }
      // If no units, assume pixels
      return parseInt(widthStr, 10) || 640;
    };

    const numericMinWidth = getNumericWidth(minWidth);
    const numericMaxWidth = getNumericWidth(maxWidth);

    const handleOnClose = useCallback(() => {
      if (onClose) {
        onClose();
      }
      dispatch(sidebarClear());
    }, [onClose, dispatch]);

    const startResizing = useCallback((e) => {
      e.preventDefault();
      if (sidebarRef.current) {
        const sidebarRect = sidebarRef.current.getBoundingClientRect();
        startXRef.current = e.clientX;
        startWidthRef.current = sidebarRect.width;
      }
      setIsResizing(true);

      // Add a transparent overlay to prevent iframe from capturing mouse events
      const overlay = document.createElement("div");
      overlay.id = "resize-overlay";
      overlay.style.position = "fixed";
      overlay.style.top = "0";
      overlay.style.left = "0";
      overlay.style.width = "100%";
      overlay.style.height = "100%";
      overlay.style.zIndex = "10000";
      overlay.style.cursor = "ew-resize";
      document.body.appendChild(overlay);
    }, []);

    const stopResizing = useCallback(() => {
      setIsResizing(false);

      // Remove the overlay
      const overlay = document.getElementById("resize-overlay");
      if (overlay) {
        document.body.removeChild(overlay);
      }

      // Check if we should auto-close the sidebar based on final width
      if (sidebarRef.current) {
        const currentWidth = sidebarRef.current.getBoundingClientRect().width;
        if (currentWidth < COLLAPSE_THRESHOLD) {
          handleOnClose();
        }
      }
    }, [handleOnClose]);

    const resize = useCallback(
      (e) => {
        if (isResizing && sidebarRef.current) {
          // Calculate the new width based on the distance moved
          const deltaX = e.clientX - startXRef.current;
          const newWidth = startWidthRef.current - deltaX;

          // Apply min/max constraints
          const clampedWidth = Math.max(
            numericMinWidth,
            Math.min(numericMaxWidth, newWidth),
          );

          // Set the width in pixels
          setWidth(`${clampedWidth}px`);
        }
      },
      [isResizing, numericMinWidth, numericMaxWidth],
    );

    // Add event listeners for resizing
    useEffect(() => {
      const handleMouseMove = (e) => {
        resize(e);
      };

      const handleMouseUp = (e) => {
        stopResizing();
      };

      if (isResizing) {
        // Use window to capture events outside the component
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
      }

      return () => {
        // Clean up event listeners when component unmounts or when isResizing changes
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }, [isResizing, resize, stopResizing]);

    const styles = useMemo(() => {
      let defaultStyles = {
        maxWidth: fixedWidth || maxWidth,
        flexGrow: 0,
        background: "#ffffff",
        flexShrink: 0,
        minWidth: fixedWidth || minWidth,
        flexBasis: fixedWidth || width,
        width: fixedWidth || width,
        overflow: "hidden",
        display: "flex",
        height: "100%",
        borderLeft: `1px solid ${blueGrey[100]}`,
        borderRight: `1px solid ${blueGrey[100]}`,
        flexDirection: "column",
        zIndex: 111,
        position: "relative", // For absolute positioning of resize handle
        // boxShadow:
        //   "rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px",
      };

      if (variant === "fixed") {
        defaultStyles = {
          ...defaultStyles,
          position: "fixed",
          top: 0,
          right: 0,
          backgroundColor: "#ffffff",
          boxShadow:
            "0 15px 35px rgba(0, 0, 0, 0.08), 0 25px 60px rgba(0, 0, 0, 0.06)",
          height: "100%",
          ".sidebar-pane-body": {
            overflowY: "auto",
          },
        };
      }
      return { ...defaultStyles, ...sx };
    }, [sx, variant, fixedWidth, maxWidth, width, minWidth]);

    const resizeHandleStyles = {
      position: "absolute",
      top: 0,
      left: 0,
      width: "3px",
      // width: "5px",
      height: "100%",
      cursor: "ew-resize",
      zIndex: 112,
      background: grey[200],

      "&:hover": {
        backgroundColor: "rgba(0, 0, 0, 0.1)",
      },

      ...(isResizing && {
        backgroundColor: "rgba(0, 0, 0, 0.15)",
      }),
    };

    return (
      <Box
        sx={{
          height: "100%",
          position: "relative",
          "& .sidebar-resize-handle-button:hover ~ .sidebar-container .sidebar-resize-handle":
            {
              backgroundColor: "rgba(0, 0, 0, 0.1)",
            },
        }}
      >
        <Box
          onMouseDown={startResizing}
          className="sidebar-resize-handle-button"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#ffffff",
            border: "1px solid #e0e0e0",
            borderRadius: "4px",
            position: "absolute",
            top: "50%",
            left: "-7px",
            width: "18px",
            overflow: "hidden",
            cursor: "w-resize",
            zIndex: 11113,
            transform: "translateY(-50%)",
            "&:hover": {
              backgroundColor: grey[200],
            },
          }}
        >
          <DragHandleRounded
            sx={{ transform: "rotate(90deg)", color: grey[600] }}
            className="thin"
          />
        </Box>

        {/* give this Box a class so our selector can find it */}
        <Box className="sidebar-container" sx={styles} ref={sidebarRef}>
          {/* the real resize handle */}
          <Box
            className="sidebar-resize-handle"
            sx={resizeHandleStyles}
            onMouseDown={startResizing}
            ref={resizeDivRef}
          />

          {isNil(title) ? null : (
            <Flx
              fw
              jb
              ac
              sx={{
                flexGrow: 0,
                // p: 1,
                // pl: 2,
                // pt: 1.5,
                // pb: 0,
                p: "16px",
                borderBottom: "1px solid rgb(238, 238, 238)",
                ...headerSx,
              }}
            >
              {icon ? (
                <Flx
                  ac
                  g={0.8}
                  sx={{ ".MuiSvgIcon-root": { fontSize: "18px" } }}
                >
                  {icon}
                  <Htag h2>{title}</Htag>
                </Flx>
              ) : (
                // <Txt fontSize={18} bold>
                //     {title}
                //   </Txt>
                <Htag sx={{ fontWeight: "bold" }} h2>
                  {title}
                </Htag>
              )}
              <IconButton size="small" onClick={handleOnClose}>
                {onCloseIcon}
              </IconButton>
            </Flx>
          )}

          {titleBottomContent}
          <Box
            className={"sidebar-pane-body"}
            sx={{
              flexGrow: 1,
              // pt: 1,
              p: bodyPadding,
              pl: bodyPadding + 0.5,
              pr: bodyPadding + 0.3,
              overflowY: bodyOverflow,
              zIndex: 111,
              ...bodySx,
            }}
            // padding={bodyPadding}
          >
            {children}
          </Box>
          {/* <ScreenContent
            className={"sidebar-pane-body"}
            sx={{
              flexGrow: 1,
              // pt: 1,
              overflowY: bodyOverflow,
              zIndex: 111,
              ...bodySx,
            }}
            padding={bodyPadding}
          >
            {children}
          </ScreenContent> */}
        </Box>
      </Box>
    );
  },
);

export default DrilldownSidebarPane;
